/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextareaAutosize,
  TextField,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useHttpClient } from "@/providers/HttpClient";
import { HttpResponse, RobotoDomainException } from "@/shared/http";

import { ResponseExplorer } from "./ResponseExplorer";

export function RestExplorer() {
  const [method, setMethod] = React.useState("GET");
  const [path, setPath] = React.useState("");
  const [body, setBody] = React.useState("");

  const [response, setResponse] = React.useState<HttpResponse | undefined>(
    undefined,
  );
  const [responseBody, setResponseBody] = React.useState<
    Body | RobotoDomainException | undefined
  >(undefined);
  const [requestExpanded, setRequestExpanded] = React.useState(true);
  const [responseExpanded, setResponseExpanded] = React.useState(false);

  const theme = useTheme();

  const handleChangeVerb = (event: SelectChangeEvent) => {
    setMethod(event.target.value);
  };

  const handleChangePath = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPath(event.target.value);
  };

  const handleChangeBody = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBody(event.target.value);
  };

  const handleChangeRequestExpanded = (
    _event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setRequestExpanded(isExpanded);
  };

  const handleChangeResponseExpanded = (
    _event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setResponseExpanded(isExpanded);
  };

  const { httpClient } = useHttpClient();

  const submitForm = (): void => {
    void (async () => {
      let response: HttpResponse | undefined = undefined;

      try {
        const url = httpClient.constructUrl(path);
        if (method === "GET") {
          response = await httpClient.get(url);
        } else if (method === "DELETE") {
          response = await httpClient.delete(url, { body: body });
        } else if (method === "POST") {
          response = await httpClient.post(url, { body: body });
        } else if (method === "PUT") {
          response = await httpClient.put(url, { body: body });
        }

        if (response) {
          setResponse(response);
          try {
            setResponseBody(await response.json());
          } catch (err) {
            setResponseBody(err as RobotoDomainException);
          }
        }
      } finally {
        setResponseExpanded(true);
      }
    })();
  };

  return (
    <Box>
      <Accordion
        expanded={requestExpanded}
        onChange={handleChangeRequestExpanded}
      >
        <AccordionSummary>Request</AccordionSummary>
        <AccordionDetails>
          <Box>
            <Box>
              <h4>Path</h4>
              <FormControl sx={{ m: theme.spacing(1) }}>
                <InputLabel id={"method-select-label"}>Method</InputLabel>
                <Select
                  labelId={"method-select-label"}
                  value={method}
                  label="Method"
                  onChange={handleChangeVerb}
                >
                  <MenuItem value={"GET"}>GET</MenuItem>
                  <MenuItem value={"PUT"}>PUT</MenuItem>
                  <MenuItem value={"POST"}>POST</MenuItem>
                  <MenuItem value={"DELETE"}>DELETE</MenuItem>
                </Select>
              </FormControl>
              <TextField
                sx={{ m: theme.spacing(1) }}
                label={"Path (After roboto.ai/)"}
                value={path}
                onChange={handleChangePath}
              />
            </Box>
            {method !== "GET" && (
              <Box>
                <h4>Body</h4>
                <TextareaAutosize
                  minRows={20}
                  value={body}
                  onChange={handleChangeBody}
                  style={{
                    color: theme.palette.text.primary,
                    fontSize: "0.9rem",
                    marginTop: theme.spacing(1),
                    width: "100%",
                    padding: theme.spacing(1.5),
                    backgroundColor: theme.palette.code.main,
                  }}
                />
              </Box>
            )}
            <Button onClick={submitForm}>Submit</Button>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={responseExpanded}
        onChange={handleChangeResponseExpanded}
      >
        <AccordionSummary>Response</AccordionSummary>
        <AccordionDetails>
          <ResponseExplorer response={response} responseBody={responseBody} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
